/**
 * IndexingPanel — Site indexing + AI summary generation controls.
 *
 * Embeds in workspace settings for one-stop onboarding:
 * configure workspace → index site → generate summaries.
 *
 * Reuses existing backend endpoints (no new API work).
 */

import React, { useState } from 'react';
import { Card, Select, Space, Typography, Progress } from 'antd';
import {
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Database,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useJobPoller } from '@/hooks/useJobPoller';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { cn } from '@/lib/utils';

const { Text, Paragraph } = Typography;

interface IndexingPanelProps {
  tenantId: string | null;
}

interface IndexStats {
  total_pages: number;
  with_summary: number;
  without_summary: number;
}

const MODEL_OPTIONS = [
  { value: 'anthropic/claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
  { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash' },
  { value: 'anthropic/claude-opus-4-6', label: 'Claude Opus 4.6' },
];

export function IndexingPanel({ tenantId }: IndexingPanelProps) {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [mode, setMode] = useState<'missing' | 'all'>('missing');
  const [model, setModel] = useState('anthropic/claude-sonnet-4-5');
  const { addNotification } = useNotificationStore();

  // Fetch stats
  const {
    data: stats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useQuery<IndexStats>({
    queryKey: ['indexing-stats', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/internal-pages/stats', {
        params: { tenant_id: tenantId },
      });
      // Stats endpoint returns per-tenant data
      const tenantStats = Array.isArray(data)
        ? data.find((s: { tenant_id: string }) => s.tenant_id === tenantId)
        : data;
      return {
        total_pages: tenantStats?.total_pages ?? 0,
        with_summary: tenantStats?.with_summary ?? 0,
        without_summary: (tenantStats?.total_pages ?? 0) - (tenantStats?.with_summary ?? 0),
      };
    },
    enabled: !!tenantId,
    staleTime: 30_000,
  });

  // Poll active job
  const { data: jobData } = useJobPoller(activeJobId);
  const isRunning = jobData?.status === 'running';
  const isDone = jobData?.status === 'done';
  const isFailed = jobData?.status === 'failed';

  // Clear job when done
  React.useEffect(() => {
    if (isDone || isFailed) {
      refetchStats();
      // Auto-clear after 10s
      const timer = setTimeout(() => setActiveJobId(null), 10_000);
      return () => clearTimeout(timer);
    }
  }, [isDone, isFailed, refetchStats]);

  const triggerPipeline = async (steps: {
    index?: boolean;
    crawl?: boolean;
    summaries?: boolean;
  }) => {
    if (!tenantId) return;
    try {
      const { data } = await apiClient.post('/internal-pages/reindex', {
        tenant_id: tenantId,
        steps_index: steps.index ?? false,
        steps_crawl: steps.crawl ?? false,
        steps_summaries: steps.summaries ?? false,
        mode,
        summary_model: steps.summaries ? model : undefined,
      });
      const jobId = data.job_id;
      setActiveJobId(jobId);

      const label = [
        steps.index && 'Index',
        steps.crawl && 'Crawl',
        steps.summaries && 'Summaries',
      ].filter(Boolean).join(' + ');

      addNotification({
        id: jobId,
        type: 'reindex',
        title: `${label} — ${tenantId}`,
        message: 'Pipeline started...',
        status: 'running',
        progress: { completed: 0, total: data.total ?? 0, failed: 0 },
      });
    } catch (err) {
      console.error('[IndexingPanel] Failed to start pipeline:', err);
    }
  };

  if (!tenantId) {
    return (
      <Card bordered={false} style={{ background: '#f9f9f9' }}>
        <div className="text-center py-8 text-muted-foreground">
          <Globe className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <Text type="secondary">Select a workspace to manage site indexing.</Text>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <Card bordered={false} style={{ background: '#f9f9f9' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <Text className="text-2xl font-bold block">{stats?.total_pages ?? '—'}</Text>
              <Text type="secondary" className="text-xs">Pages Indexed</Text>
            </div>
            <div className="text-center">
              <Text className="text-2xl font-bold text-teal block">{stats?.with_summary ?? '—'}</Text>
              <Text type="secondary" className="text-xs">With Summary</Text>
            </div>
            <div className="text-center">
              <Text className="text-2xl font-bold text-coral block">{stats?.without_summary ?? '—'}</Text>
              <Text type="secondary" className="text-xs">Missing Summary</Text>
            </div>
          </div>

          {statsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </Card>

      {/* Controls */}
      <Card bordered={false} style={{ background: '#f9f9f9' }}>
        <Space direction="vertical" size="middle" className="w-full">
          {/* Options row */}
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <Text type="secondary" className="text-xs block mb-1">Mode</Text>
              <Select
                value={mode}
                onChange={setMode}
                size="small"
                style={{ width: 140 }}
                disabled={isRunning}
                options={[
                  { value: 'missing', label: 'New pages only' },
                  { value: 'all', label: 'Redo all' },
                ]}
              />
            </div>
            <div>
              <Text type="secondary" className="text-xs block mb-1">Summary Model</Text>
              <Select
                value={model}
                onChange={setModel}
                size="small"
                style={{ width: 200 }}
                disabled={isRunning}
                options={MODEL_OPTIONS}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              className="gap-2 bg-coral hover:bg-coral-hover text-white"
              disabled={isRunning}
              onClick={() => triggerPipeline({ index: true, crawl: true, summaries: true })}
            >
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Index & Summarize
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={isRunning}
              onClick={() => triggerPipeline({ index: true, crawl: true })}
              className="gap-1.5"
            >
              <Database className="h-3.5 w-3.5" />
              Index Only
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={isRunning}
              onClick={() => triggerPipeline({ summaries: true })}
              className="gap-1.5"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Summaries Only
            </Button>
          </div>
        </Space>
      </Card>

      {/* Progress card */}
      {activeJobId && jobData && (
        <Card
          bordered={false}
          className={cn(
            'border-l-[3px]',
            isRunning && 'border-l-amber-400',
            isDone && 'border-l-teal',
            isFailed && 'border-l-destructive',
          )}
          style={{ background: '#f9f9f9' }}
        >
          <div className="flex items-center gap-3 mb-2">
            {isRunning && <Loader2 className="h-4 w-4 animate-spin text-amber-500 dark:text-amber-400" />}
            {isDone && <CheckCircle2 className="h-4 w-4 text-teal" />}
            {isFailed && <AlertCircle className="h-4 w-4 text-destructive" />}
            <Text strong className="text-sm">{jobData.title}</Text>
          </div>

          {isRunning && (
            <Progress
              percent={jobData.total > 0 ? Math.round((jobData.completed / jobData.total) * 100) : 0}
              size="small"
              status="active"
              format={() => `${jobData.completed}/${jobData.total}`}
            />
          )}

          {isDone && (
            <Text type="secondary" className="text-xs">
              Completed: {jobData.completed}/{jobData.total}
              {jobData.failed > 0 && ` (${jobData.failed} failed)`}
            </Text>
          )}

          {isFailed && (
            <Text type="danger" className="text-xs">
              {jobData.error || 'Pipeline failed'}
            </Text>
          )}
        </Card>
      )}
    </div>
  );
}
